<script setup lang="ts">
import { ref } from 'vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import { SEVERITY_BADGE, SEVERITY_LABEL } from './issue-enums';

interface Props {
  current: number;
  disabled?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{ change: [severity: number] }>();

const isOpen = ref(false);
const SEVERITIES = [0, 1, 2, 3] as const;

function select(s: number) {
  if (s !== props.current) emit('change', s);
  isOpen.value = false;
}
</script>

<template>
  <div class="relative inline-block">
    <button
      :disabled="disabled"
      class="flex items-center gap-1.5 px-2 py-1 border border-border rounded-md bg-surface hover:bg-canvas-soft transition-colors text-caption disabled:opacity-50 disabled:pointer-events-none"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="isOpen = !isOpen"
    >
      <BaseBadge :variant="SEVERITY_BADGE[current] ?? 'info'">
        {{ SEVERITY_LABEL[current] ?? String(current) }}
      </BaseBadge>
      <svg class="w-3.5 h-3.5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      role="listbox"
      :aria-label="`Change severity from ${SEVERITY_LABEL[current]}`"
      class="absolute top-full left-0 mt-1 z-20 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[140px]"
    >
      <button
        v-for="s in SEVERITIES"
        :key="s"
        role="option"
        :aria-selected="s === current"
        class="w-full flex items-center gap-2 px-3 py-1.5 text-caption hover:bg-canvas-soft transition-colors text-left"
        @click="select(s)"
      >
        <BaseBadge :variant="SEVERITY_BADGE[s] ?? 'info'">
          {{ SEVERITY_LABEL[s] }}
        </BaseBadge>
        <svg v-if="s === current" class="w-3 h-3 text-navy-600 ml-auto" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- Click-outside to close -->
    <div v-if="isOpen" class="fixed inset-0 z-10" aria-hidden="true" @click="isOpen = false" />
  </div>
</template>
